
import { NODE, ATTRIBUTE_VIEW, RELATION_VIEW, VIEWTYPE, QUERY } from 'Types'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'
import { Loading } from 'Components'
import Graph from './Graph'
import GraphConsole from './Graph/GraphConsole'
import GraphContextMenu from './GraphContextMenu'
import { useCancelable, useQuery, usePrevious } from 'Hooks'
import { Avatar } from 'antd'
import { getUid, splitUid, WeaverQuery, getMemberKey, isBirthday } from 'Services/WeaverUtils'
import { setEditingNode, setTab, setTabDisabled, setDetailPopup } from 'Actions/explore'
import balloonIcon from 'Images/undraw_floating.svg'
import birthdayHat from 'Images/birthday_hat.svg'
import './index.scss'
import { setDarkModeEnabled } from 'Actions/explore'
import {
  loadGraphData,
  loadAbstractGraphData,
  loadAbstractRelationData,
  loadAbstractAttributeData,
  buildGraphData,
  showHide,
  buildGraphGroups,
  preApplyFilters,
  propertyCount
} from './Utils'
import {
  clearGraph,
  removeGraphEdge,
  setGraphNodes,
  updateGraphNodes,
  setGraphGroups
} from 'Actions/graph'
import {
  setFilteredNodesByOther,
  resetFilteredNodes,
  resetFilteredRelations,
  resetGraphFilters,
  resetRelationFilters,
  resetTypeFilters,
  setTypeFilters,
  updateTypeFilter,
  updateTypeFilters
} from 'Actions/graphFilter'
import {
  filterNodesByGraph,
  filterNodesByRelation,
  filterNodesByType,
  filterRelationsByKey,
  flatMapUnique
} from 'Services/GraphFilterUtils'
import { useGraphData } from '../Context'
import {
  setCanonicalNodeIds,
  setVisToWvrMap,
  setGraphAddingEdge,
  setShowAttributes,
  setShowAllMetaNodes,
  setShowRelevantMetaNodes,
  setQueryResults,
  setRichNodesMap
} from '../Context/actions'
import WeaverService from 'Services/WeaverService'

const GraphViewContainer = ({
  currentBranch,
  clearGraph,
  currentProject,
  currentItem,
  devtoolsEnabled,
  graphData,
  isHidden,
  left,
  setGraphNodes,
  setGraphGroups,
  updateGraphNodes,
  filteredNodes,
  filteredRelations,
  graphFilters,
  relationFilters,
  typeFilters,
  tabIsLocked,
  setDarkModeEnabled,
  nameKeys
}) => {
  const [loading, setLoading] = useState(true)
  const [active, setActive] = useState(undefined)
  const [data, setData] = useState(currentItem)
  const [viewType, setViewType] = useState(undefined)
  const [memberKey, setMemberKey] = useState(getMemberKey())
  const [isPublic, setIsPublic] = useState(false)
  const currentPendingQuery = useRef(undefined)
  const [graphCtx, ctxDispatch] = useGraphData()
  const { canonicalNodeIds, showAttributes, showAllMetaNodes, showRelevantMetaNodes, queryResults, richNodesMap } = graphCtx
  const dispatch = useDispatch()

  const params = useQuery()
  const { params: { spaceId }} = useRouteMatch()

  const mode = params.get('mode')
  mode === 'dark' && setDarkModeEnabled(true)

  const prevFilteredData = usePrevious({ filteredNodes, filteredRelations })

  useLayoutEffect(() => {
    ctxDispatch(setShowAttributes(false))
    localStorage.setItem('showAttributes', false)
    ctxDispatch(setShowAllMetaNodes(false))
    localStorage.setItem('showAllMetaNodes', false)
    ctxDispatch(setShowRelevantMetaNodes(false))
    localStorage.setItem('showRelevantMetaNodes', false)
  }, [currentProject])

  useEffect(() => {
    (async () => {
      const currProj = await Weaver.Project.load(spaceId)
      const acl = await currProj.getACL()
      const spaceIsPublic = await acl.getPublicReadAccess()
      if (spaceIsPublic) {
        setIsPublic(true)
      } else {
        setIsPublic(false)
      }
      setData(currentItem)
    })()
  }, [currentItem])

  useEffect(() => {
    (async () => {
      if (data.type === QUERY) {
        await handleQuery()
      } else if (data.type === NODE) {
        await handleNode(data)
      } else if (data.type === RELATION_VIEW || data.type === ATTRIBUTE_VIEW) {
        await handleAbstract(data)
      }
    })()
  }, [data, currentBranch])

  useLayoutEffect(() => {
    (async () => {
      clearGraph()
      await WeaverService.updateTypesMap()
      const graphGroups = await buildGraphGroups()
      setGraphGroups(graphGroups)
    })()
  }, [currentProject, currentBranch])

  const updateRichNodesMap = (nodes) => {
    const isRich = (n) => {
      const { toNode, fromNode, value } = n
      if (n instanceof Weaver.AttributeNode) {
        return (fromNode && value !== undefined)
      } else if (n instanceof Weaver.RelationNode) {
        return (toNode && fromNode)
      }
    }

    const visitedNodes = []
    for (const node of nodes) {
      node._traverseNode((n, _) => {
        if (n instanceof Weaver.AttributeNode || n instanceof Weaver.RelationNode) {
          if (!isRich(n)) {
            return
          }
        }
        const existing = richNodesMap[getUid(n)]
        if (propertyCount(n) > propertyCount(existing) || (n._loaded && !existing?._loaded)) {
          richNodesMap[getUid(n)] = n
        }
      }, visitedNodes)
    }
    return richNodesMap
  }

  useEffect(() => {
    if (!queryResults.abstract) {
      const _richNodesMap = updateRichNodesMap(queryResults)
      ctxDispatch(setRichNodesMap(_richNodesMap))
      const queryResultIds = queryResults.map(getUid)
      ctxDispatch(setCanonicalNodeIds(_.uniq([...canonicalNodeIds, ...queryResultIds])))
    } else {
      ctxDispatch(setCanonicalNodeIds(Object.keys(queryResults.nodes)))
    }
  }, [queryResults])

  useEffect(() => {
    preApplyFilters()
    if (queryResults.abstract) {
      dispatch(setGraphNodes({ nodes: queryResults.nodes, edges: queryResults.edges }))
    } else {
      const { visWvrMap } = showHide(canonicalNodeIds, richNodesMap, { showAttributes, showRelevantMetaNodes, showAllMetaNodes })
      ctxDispatch(setVisToWvrMap(visWvrMap))
      const gData = buildGraphData(visWvrMap)
      const withFiltered = applyFilter(gData)
      dispatch(setGraphNodes(withFiltered))
    }
    return () => {
      clearGraph()
      setCanonicalNodeIds([])
      setRichNodesMap({})
    }
  }, [canonicalNodeIds, showAttributes, showAllMetaNodes, showRelevantMetaNodes])

  useEffect(() => {
    (async () => {
      dispatch(await filterNodesByType())
    })();
  }, [typeFilters])

  useEffect(() => {
    (async () => {
      dispatch(await filterNodesByRelation())
      dispatch(await filterRelationsByKey())
    })();
  }, [relationFilters])

  useEffect(() => {
    (async () => {
      dispatch(await filterNodesByGraph())
    })();
  }, [graphFilters])

  useEffect(() => {
    if (
      !_.isEqual(prevFilteredData?.filteredNodes, filteredNodes) ||
      !_.isEqual(prevFilteredData?.filteredRelations, filteredRelations)
    ) {
      const filteredItems = applyFilter(graphData)
      dispatch(updateGraphNodes(filteredItems))
    }
    setTimeout(() => setLoading(false), 500)
  }, [filteredNodes, filteredRelations])

  const handleQuery = async () => {
    ctxDispatch(setGraphAddingEdge(false))
    const query = data.data.query
    setViewType(data.data.viewType)
    setMemberKey(data.data.memberKey || getMemberKey())
    await runGraphQuery(query, undefined, data.data.viewType === VIEWTYPE.ABSTRACT, data.data.memberKey)
  }

  const handleNode = async (data) => {
    const [id, graph] = splitUid(data.wvrId || data.id)
    let query;
    if (!graph === undefined) {
      query = new WeaverQuery()
        .restrict([id])
        .inGraph(graph)
        .alwaysLoadAttributes(nameKeys)
    } else {
      query = new WeaverQuery()
        .restrict([id])
        .alwaysLoadAttributes(nameKeys)
    }
    setViewType(data.viewType)
    await runGraphQuery(query, data.id, data.viewType === VIEWTYPE.ABSTRACT, data.memberKey)
  }

  const handleAbstract = async (data) => {
    setViewType(data.viewType)
    await runGraphQuery(null, data.id, data.viewType === VIEWTYPE.ABSTRACT, null, data.type)
  }

  const applyFilter = (gData) => {
    const { nodes, edges } = gData

    const nodesToHide = flatMapUnique(filteredNodes)
    const relationsToHide = flatMapUnique(filteredRelations)

    const hiddenNodes = {}
    const hiddenEdges = {}

    for (const id in nodes) {
      if (nodesToHide.includes(id)) {
        hiddenNodes[id] = {
          ...nodes[id],
          hidden: true
        }
      } else {
        hiddenNodes[id] = {
          ...nodes[id],
          hidden: false
        }
      }
    }

    for (const id in edges) {
      if (relationsToHide.includes(id)) {
        hiddenEdges[id] = {
          ...edges[id],
          hidden: true
        }
      } else {
        hiddenEdges[id] = {
          ...edges[id],
          hidden: false
        }
      }
    }

    return { nodes: hiddenNodes, edges: hiddenEdges }
  }

  const resetFilters = () => {
    dispatch(resetTypeFilters())
    dispatch(resetRelationFilters())
    dispatch(resetGraphFilters())
  }

  const runGraphQuery = async (query, id, abstract, memberKey, type) => {
    setLoading(true)
    clearGraph()
    ctxDispatch(setCanonicalNodeIds([]))
    ctxDispatch(setRichNodesMap({}))
    resetFilters()
    try {
      currentPendingQuery.current?.cancel()
      let promise
      if (type === RELATION_VIEW) {
        promise = useCancelable(await loadAbstractRelationData(id))
      } else if (type === ATTRIBUTE_VIEW) {
        promise = useCancelable(await loadAbstractAttributeData(id))
      } else if (abstract) {
        promise = useCancelable(await loadAbstractGraphData(id, memberKey))
      } else {
        promise = useCancelable(await loadGraphData(query, showAttributes, showAllMetaNodes, id))
      }
      currentPendingQuery.current = promise
      const loadedGraphData = await promise
      ctxDispatch(setQueryResults(loadedGraphData))
    } catch (e) {
      console.error(e)
    }
  }

  const renderGraph = () => {
    if (_.values(graphData.nodes)[0]) {
      return (
        <div data-testid="graph-container">
          <Graph
            activeItems={[active, setActive]}
            viewType={viewType}
            memberKey={memberKey}
            tabIsLocked={tabIsLocked}
          />
        </div>
      )
    } else {
      return (
        <div
          data-testid="graph-container"
          className="graph-embossed-logo-container"
        >
          <div>
            <Avatar
              src={balloonIcon}
              style={{ width: 340, height: 280 }}
            />
            {
              isBirthday() &&
                <img src={birthdayHat}
                  style={{
                    position: 'absolute',
                    width: '9px',
                    left: '221px',
                    top: '217px',
                    zIndex: 1,
                    transform: 'rotate(39deg)'
                  }}
                />
            }
          </div>
        </div>
      )
    }
  }

  return (
    <>
      <GraphContextMenu data={{ graphData, active, setActive }}>
        { loading ? <Loading style={{ stroke: 'rgb(229, 234, 236)' }} /> : renderGraph() }
      </GraphContextMenu>
      { ((devtoolsEnabled && currentProject) && currentBranch) && <GraphConsole left={left} isHidden={isHidden} /> }
    </>
  )
}

export default connect(({ graph, graphFilter, explore, projects, branches }) =>
  ({
    filteredNodes: graphFilter.filteredNodes,
    filteredRelations: graphFilter.filteredRelations,
    disabledRelations: explore.disabledRelations,
    darkModeEnabled: explore.darkModeEnabled,
    graphData: graph.graphData,
    currentItem: explore.currentItem,
    currentTab: explore.currentTab,
    graphFilters: graphFilter.graphFilters,
    relationFilters: graphFilter.relationFilters,
    tabIsLocked: explore.tabIsLocked,
    typeFilters: graphFilter.typeFilters,
    currentProject: projects.currentProject,
    devtoolsEnabled: graph.devtoolsEnabled,
    currentBranch: branches.currentBranch,
    tabsDisabled: explore.tabsDisabled,
    nameKeys: explore.nameKeys,
  })
, {
  clearGraph,
  removeGraphEdge,
  resetFilteredNodes,
  resetFilteredRelations,
  resetGraphFilters,
  resetRelationFilters,
  setTypeFilters,
  setDetailPopup,
  setEditingNode,
  setFilteredNodesByOther,
  setTab,
  setTabDisabled,
  setGraphNodes,
  setGraphGroups,
  updateGraphNodes,
  updateTypeFilter,
  updateTypeFilters,
  setDarkModeEnabled
})(GraphViewContainer)
